import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, LayoutGrid } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import { resolveBrand } from "../../lib/brand";
import { DOORS, isExternal } from "../../pages/Ecosystem/doors";

const SERIF = '"Cormorant Garamond", serif';
const SANS = '"Manrope", sans-serif';
const GOLD = "#D7BB7A";
const CREAM = "#F3EDDD";
// On the Soulumo / One Million Lights domains "/" is their own front door, so Reclaim home lives at reclaim.app.
export const reclaimHome = () => (resolveBrand() === "reclaim" ? "/" : "https://reclaim.app");

export const goTo = (href, nav) => {
  if (isExternal(href)) window.location.href = href;
  else nav(href);
};

const DrawerRow = ({ d, here, onGo }) => (
  <button type="button" onClick={onGo} data-testid={`eco-drawer-${d.id}`}
    className="group w-full flex items-center gap-4 py-4 text-left border-b transition-colors duration-300 hover:bg-white/[0.03]" style={{ borderColor: "rgba(215,187,122,0.12)" }}>
    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.accent, boxShadow: `0 0 12px ${d.accent}` }} />
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2 text-xl" style={{ fontFamily: SERIF, color: CREAM }}>
        {d.name}{here && <span className="text-[0.55rem] uppercase tracking-[0.22em]" style={{ color: GOLD, fontFamily: SANS }}>You are here</span>}
      </span>
      <span className="block text-sm truncate" style={{ color: "rgba(243,237,221,0.55)", fontFamily: SANS }}>{d.line}</span>
    </span>
    <ArrowUpRight size={16} className="opacity-40 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: GOLD }} />
  </button>
);

export const ReclaimDrawer = ({ open, onOpenChange, current }) => {
  const nav = useNavigate();
  const go = (d) => { onOpenChange(false); goTo(d.id === "reclaim" ? reclaimHome() : d.href, nav); };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" overlayClassName="z-[125]" className="w-full sm:max-w-md border-l overflow-y-auto z-[130]" data-testid="ecosystem-drawer"
        style={{ background: "#070D0B", borderColor: "rgba(215,187,122,0.18)", color: CREAM }}>
        <p className="text-[0.6rem] uppercase tracking-[0.34em] mt-2" style={{ color: GOLD, fontFamily: SANS }}>The Reclaim ecosystem</p>
        <SheetTitle className="mt-3 text-4xl font-light" style={{ fontFamily: SERIF, color: CREAM }}>There is another way.</SheetTitle>
        <SheetDescription className="mt-2 text-sm" style={{ color: "rgba(243,237,221,0.6)", fontFamily: SANS }}>
          Different doors, one purpose. You're part of something bigger.
        </SheetDescription>
        <div className="mt-6">{DOORS.map((d) => <DrawerRow key={d.id} d={d} here={d.id === current} onGo={() => go(d)} />)}</div>
        <p className="mt-8 text-lg italic" style={{ fontFamily: SERIF, color: "rgba(243,237,221,0.7)" }}>Using technology to help us become more human, not less.</p>
      </SheetContent>
    </Sheet>
  );
};

// "SOULUMO · by RECLAIM" — tapping RECLAIM opens the ecosystem drawer.
export const ByReclaim = ({ product, label = null, showName = true, className = "", color = GOLD }) => {
  const [open, setOpen] = useState(false);
  const d = DOORS.find((x) => x.id === product);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="by-reclaim-button"
        className={`inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.26em] transition-opacity duration-300 hover:opacity-100 opacity-80 ${className}`}
        style={{ color, fontFamily: SANS }}>
        {label ? <><LayoutGrid size={13} /> {label}</> : <>{d && showName && product !== "reclaim" && <span style={{ opacity: 0.7 }}>{d.name} ·</span>} by <span className="font-semibold">Reclaim</span></>}
      </button>
      <ReclaimDrawer open={open} onOpenChange={setOpen} current={product} />
    </>
  );
};

// Slim strip for product front pages that have no header of their own to host the tag.
export const ReclaimBar = ({ product, tone = "dark" }) => {
  const [open, setOpen] = useState(false);
  const d = DOORS.find((x) => x.id === product);
  const fg = tone === "dark" ? CREAM : "#1d1a14";
  return (
    <div className="relative z-[30] w-full flex items-center justify-between px-4 h-9" data-testid="reclaim-bar"
      style={{ borderBottom: "1px solid rgba(215,187,122,0.14)", background: tone === "dark" ? "rgba(7,13,11,0.55)" : "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)" }}>
      <button type="button" onClick={() => setOpen(true)} data-testid="reclaim-bar-tag" className="text-[0.66rem] uppercase tracking-[0.26em] font-medium" style={{ color: fg, fontFamily: SANS }}>
        {d?.name} <span style={{ color: GOLD }}>· by <b className="font-semibold">Reclaim</b></span>
      </button>
      <button type="button" onClick={() => setOpen(true)} aria-label="Explore the Reclaim ecosystem" data-testid="reclaim-bar-open" className="opacity-70 hover:opacity-100 transition-opacity duration-300" style={{ color: GOLD }}>
        <LayoutGrid size={15} />
      </button>
      <ReclaimDrawer open={open} onOpenChange={setOpen} current={product} />
    </div>
  );
};
